import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLearner } from '../../hooks/useLearner';
import { useProgress } from '../../hooks/useProgress';
import useAudioPrefs from '../../hooks/useAudioPrefs';
import { getBuddyPortraitPath } from '../../lib/buddyAssets';
import LevelRing from '../../components/map/LevelRing';
import navBookshelfIcon from '../../assets/icons/nav-bookshelf.webp';
import navWorkbookIcon from '../../assets/icons/nav-workbook.webp';
import navActivitiesIcon from '../../assets/icons/nav-activities.webp';
import s from './StoryShipOverlay.module.css';

const DESKTOP_BREAKPOINT = 1280;
const SPRING = { type: 'spring', stiffness: 380, damping: 36 };

// Static speaker icon — muted shows the slash, unmuted shows the sound waves.
// No animation (a simple icon swap per BP-AUDIO-SYSTEM-01).
function SpeakerIcon({ muted }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 5 6 9H2v6h4l5 4V5z" />
      {muted ? (
        <>
          <line x1="22" y1="9" x2="16" y2="15" />
          <line x1="16" y1="9" x2="22" y2="15" />
        </>
      ) : (
        <>
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
          <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
        </>
      )}
    </svg>
  );
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' && window.innerWidth >= DESKTOP_BREAKPOINT
  );
  useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
    const onChange = (e) => setIsDesktop(e.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);
  return isDesktop;
}

/**
 * Story Ship navigation overlay. Triggered from the floating ship button (mobile)
 * or the "Story Ship" card in LearnerSidebar (desktop).
 *
 * Mobile: fullscreen, slides up from bottom (matches Figma 2715:2539).
 * Desktop: centered modal, scale/fade in (smaller than fullscreen).
 *
 * Nav items map to existing routes — see App.jsx. "Activities" currently
 * routes to /workbook since the games hub lives there; a dedicated route
 * can replace it later without changing this overlay.
 */
export default function StoryShipOverlay({ open, onClose }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const { data: activeLearner } = useLearner();
  const { data: progress } = useProgress(activeLearner?.id);
  const { muted, toggleMute } = useAudioPrefs();

  const buddyImg = getBuddyPortraitPath(activeLearner?.buddy?.slug);
  const currentLevel = progress?.level?.current ?? 0;
  const nextLevel = currentLevel + 1;
  const xpProgressPct = progress?.level?.xp_progress_pct ?? 0;
  const streak = progress?.streak?.current ?? 0;
  const knownCount = progress?.vocab?.known_count ?? 0;
  const curriculumSize = progress?.vocab?.curriculum_size ?? null;
  const xpAtNextLevel = progress?.level?.xp_at_next_level ?? 0;
  const xpTotal = progress?.level?.xp_total ?? 0;
  const xpToNext = Math.max(0, xpAtNextLevel - xpTotal);

  const go = (path) => {
    onClose();
    navigate(path);
  };

  // Activities icon is presented horizontally mirrored in the Figma design
  // (`-scale-y-100 rotate-180` = net `scaleX(-1)`). `mirror: true` opts in to
  // that flip on the rendered <img>.
  const navItems = [
    { key: 'bookshelf', label: t('map.bookshelf'), icon: navBookshelfIcon, path: '/bookshelf', w: 69, h: 91 },
    { key: 'workbook', label: t('map.workbook'), icon: navWorkbookIcon, path: '/workbook', w: 66, h: 72 },
    { key: 'activities', label: t('map.activities'), icon: navActivitiesIcon, path: '/workbook', w: 90, h: 62, mirror: true },
  ];

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className={s.scrim}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className={`${s.panel} ${isDesktop ? s.panelDesktop : s.panelMobile}`}
            role="dialog"
            aria-label="Story Ship menu"
            initial={isDesktop ? { opacity: 0, scale: 0.92 } : { y: '100%' }}
            animate={isDesktop ? { opacity: 1, scale: 1 } : { y: 0 }}
            exit={isDesktop ? { opacity: 0, scale: 0.92 } : { y: '100%' }}
            transition={SPRING}
          >
            <button
              type="button"
              className={s.closeBtn}
              onClick={onClose}
              aria-label="Close menu"
            >
              ✕
            </button>

            <div className={s.topRightControls}>
              <button
                type="button"
                className={s.muteBtn}
                onClick={toggleMute}
                aria-pressed={muted}
                aria-label={muted ? t('audio.unmute') : t('audio.mute')}
                title={muted ? t('audio.unmute') : t('audio.mute')}
              >
                <SpeakerIcon muted={muted} />
              </button>
              <button
                type="button"
                className={s.accountBtn}
                onClick={() => go('/profile')}
              >
                {t('map.account')}
              </button>
            </div>

            <div className={s.content}>
              {!isDesktop && (
                <>
                  <div className={s.identity}>
                    <div className={s.avatar}>
                      {buddyImg && <img src={buddyImg} alt="" className={s.avatarImg} />}
                    </div>
                    <p className={s.name}>{activeLearner?.name ?? ''}</p>
                    <p className={s.subtitle}>{activeLearner?.curriculum?.name || activeLearner?.curriculum?.slug || 'Framework'}</p>
                  </div>

                  <div className={s.statsBlock}>
                    <LevelRing level={currentLevel} progressPct={xpProgressPct} />
                    <div className={s.statRows}>
                      <p className={s.statRow}>
                        <span className={s.statAccent}>
                          {curriculumSize ? `${knownCount}/${curriculumSize}` : `${knownCount}`}
                        </span>{' '}
                        {t('map.charactersKnown')}
                      </p>
                      <p className={s.statRowSub}>
                        {t('map.xpToLevel', { xp: xpToNext.toLocaleString(), level: nextLevel })}
                      </p>
                    </div>

                    <div className={s.streakRow}>
                      <span aria-hidden="true">🔥</span>
                      <span className={s.streakText}>{t('map.dayStreak', { count: streak })}</span>
                    </div>
                  </div>
                </>
              )}

              <div className={s.navGrid}>
                {navItems.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={s.navItem}
                    onClick={() => go(item.path)}
                  >
                    <img
                      src={item.icon}
                      alt=""
                      width={item.w}
                      height={item.h}
                      className={`${s.navIcon} ${item.mirror ? s.navIconMirror : ''}`}
                    />
                    <span className={s.navLabel}>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
